import { Icon, Menu, TextProps, Portal } from '@chakra-ui/react';
import { Message } from '@monetize/types/app';
import { format } from 'date-fns/format';
import {
  Dispatch,
  FC,
  memo,
  SetStateAction,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';
import {
  MdCheck,
  MdContentCopy,
  MdErrorOutline,
  MdOutlineFileDownload,
} from 'react-icons/md';
import { useNavigatorPermission } from '../../hooks/useNavigatorPermission';
import { useChatSocket } from '../../services/chatSocket';
import { copyToClipboard } from '../../utils';
import {
  MAccordion,
  MAccordionCustomButton,
  MAccordionItem,
  MAccordionPanel,
  MBox,
  MButton,
  MFlex,
  MIconButton,
  MText,
  MTooltip,
} from '../Monetize';
import { MarkdownRenderer } from './MarkdownRenderer';
import { MontyAvatar } from './MontyAvatar';

interface MessageItemProps {
  message: Message;
  /** Off when the turn's activity header above already shows Monty. */
  showAvatar?: boolean;
  /** The conversation's last reply keeps its actions visible; the rest
   * reveal theirs on hover or focus. */
  isLatestReply?: boolean;
}

/** Message actions stay out of the way until wanted; devices that can't
 * hover always show them. Timestamps always show: a message with nothing to
 * focus would otherwise hide its time from keyboard users. */
const revealOnHoverProps = (isAlwaysVisible: boolean) =>
  ({
    opacity: isAlwaysVisible ? 1 : 0,
    transition: 'opacity 0.15s',
    _groupHover: { opacity: 1 },
    _groupFocusWithin: { opacity: 1 },
    css: { '@media (hover: none)': { opacity: 1 } },
  }) as const;

// Module-scope so MarkdownRenderer's memoized `components` map stays stable
// across re-renders (e.g. every streamed token) instead of remounting the tree.
const USER_TEXT_PROPS: TextProps = { fontSize: 'sm', color: 'white' };
const ASSISTANT_TEXT_PROPS: TextProps = { fontSize: 'sm', color: 'fg.primary' };

interface FormatTextProps {
  text: string;
  textProps?: TextProps;
  setTablesCsv: Dispatch<SetStateAction<string[]>>;
}

const FormatText: FC<FormatTextProps> = ({ text, textProps, setTablesCsv }) => {
  if (!text) {
    return null;
  }

  return (
    <MBox
      css={{
        '& > *:last-child': { mb: 0 },
        'ul *, ol *': { mb: '0' },
        'li:has(ul), li:has(ol)': {
          mb: 2,
        },
        'p > *': { mb: 0 },
      }}
    >
      <MarkdownRenderer
        text={text}
        textProps={textProps}
        setTablesCsv={setTablesCsv}
      />
    </MBox>
  );
};

const AssistantAvatar: FC<{ hasError?: boolean }> = ({ hasError }) => {
  if (!hasError) {
    return <MontyAvatar boxSize={7} />;
  }
  return (
    <MFlex
      alignItems="center"
      justifyContent="center"
      boxSize={7}
      borderRadius="lg"
      flexShrink={0}
      bg="tRed.50"
      color="tRed.base"
    >
      <Icon as={MdErrorOutline} boxSize={4} />
    </MFlex>
  );
};

interface MessageErrorDetailsProps {
  message: Message;
}

// Split out of MessageItem so only error bubbles subscribe to the chat
// context (session id, retry, loading) -- every other message stays free of
// it and can be memoized, instead of re-rendering on every streamed token.
const MessageErrorDetails: FC<MessageErrorDetailsProps> = ({ message }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { sessionId, retryMessage, isLoading } = useChatSocket();

  return (
    <>
      <MAccordion collapsible mt={3}>
        <MAccordionItem
          value="0"
          borderColor="tRed.cosmos"
          borderRadius="md"
          overflow="hidden"
        >
          <MAccordionCustomButton
            isExpanded={isExpanded}
            label="Error details"
            onClick={() => setIsExpanded(!isExpanded)}
            labelProps={{ fontSize: 'sm' }}
          />
          <MAccordionPanel>
            <MText fontSize="xs" mt={2}>
              <strong>Session ID:</strong> {sessionId}
            </MText>
            <MText fontSize="xs" mt={1}>
              <strong>Error type:</strong> {message.error?.type || 'Unknown'}
            </MText>
            <MText fontSize="xs" mt={1}>
              <strong>Error message:</strong>{' '}
              {message.error?.message || 'An unknown error occurred.'}
            </MText>
          </MAccordionPanel>
        </MAccordionItem>
      </MAccordion>
      {message.prompt && (
        <MButton
          mt={3}
          size="sm"
          variant="primary"
          isDisabled={isLoading}
          onClick={() => retryMessage(message.id)}
        >
          Retry
        </MButton>
      )}
    </>
  );
};

const MessageItemComponent: FC<MessageItemProps> = ({
  message,
  showAvatar = true,
  isLatestReply = false,
}) => {
  const [tablesCsv, setTablesCsv] = useState<string[]>([]);
  const [isCopied, setIsCopied] = useState(false);
  const copyResetRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const { canCopyClipboard } = useNavigatorPermission();
  const downloadMenuTriggerId = useId();

  useEffect(() => () => clearTimeout(copyResetRef.current), []);

  const handleCopy = async () => {
    const didCopy = await copyToClipboard(message.content);
    if (!didCopy) {
      return;
    }
    setIsCopied(true);
    clearTimeout(copyResetRef.current);
    copyResetRef.current = setTimeout(() => setIsCopied(false), 2000);
  };

  const handleDownload = (csv: string, filename: string) => {
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const isUser = message.role === 'user';
  const isError = message.type === 'error';

  if (isUser) {
    return (
      <MFlex
        className="group"
        direction="column"
        alignItems="flex-end"
        alignSelf="flex-end"
        maxW="85%"
      >
        <MBox
          px={4}
          py={2.5}
          borderRadius="2xl"
          borderBottomRightRadius="md"
          bgGradient="linear-gradient(135deg, {colors.tIndigo.base} 0%, #5C6FE8 100%)"
          color="white"
          boxShadow="0 4px 12px rgba(70, 89, 218, 0.25)"
          maxW="100%"
          css={{
            '& p': { color: 'white !important' },
            '& a': { color: 'white !important', textDecoration: 'underline' },
          }}
        >
          <FormatText
            text={message.content}
            textProps={USER_TEXT_PROPS}
            setTablesCsv={setTablesCsv}
          />
        </MBox>
        <MText fontSize="xxs" color="fg.subtle" mt={1} mr={1}>
          {format(new Date(message.timestamp), 'h:mm a')}
        </MText>
      </MFlex>
    );
  }

  const downloadAction =
    tablesCsv.length === 0 ? null : tablesCsv.length === 1 ? (
      <MTooltip label="Download table CSV" hasArrow placement="top">
        <MIconButton
          aria-label="Download CSV"
          icon={<MdOutlineFileDownload size="16px" />}
          size="xs"
          variant="ghost"
          color="fg.muted"
          _hover={{ color: 'fg.strong', bg: 'bg.hover' }}
          onClick={() => handleDownload(tablesCsv[0], 'table.csv')}
        />
      </MTooltip>
    ) : (
      // Both the menu and the tooltip set the trigger's `id` and look
      // the trigger up by it; without a shared id the tooltip's wins
      // and the menu can't find its anchor to position against.
      <Menu.Root
        lazyMount
        unmountOnExit
        ids={{ trigger: downloadMenuTriggerId }}
      >
        <MTooltip
          label="Download tables"
          hasArrow
          placement="top"
          ids={{ trigger: downloadMenuTriggerId }}
        >
          {/* `unstyled` — the menu recipe's trigger styles would
           * otherwise override this button's muted color and hover.
           * v2's `MenuButton as={MIconButton}` never applied them. */}
          <Menu.Trigger asChild unstyled>
            <MIconButton
              aria-label="Download tables"
              icon={<MdOutlineFileDownload size="16px" />}
              size="xs"
              variant="ghost"
              color="fg.muted"
              _hover={{ color: 'fg.strong', bg: 'bg.hover' }}
            ></MIconButton>
          </Menu.Trigger>
        </MTooltip>
        <Portal>
          <Menu.Positioner>
            <Menu.Content>
              {tablesCsv.map((csv, idx) => (
                <Menu.Item
                  key={idx}
                  onSelect={() => handleDownload(csv, `table_${idx + 1}.csv`)}
                  value={`table-${idx}`}
                >
                  Download table {idx + 1}
                </Menu.Item>
              ))}
            </Menu.Content>
          </Menu.Positioner>
        </Portal>
      </Menu.Root>
    );

  // Replies sit on the page at full width; only errors keep a box, so they
  // still stand out from the conversation.
  return (
    <MFlex className="group" direction="column" gap={1.5} width="100%">
      {showAvatar && <AssistantAvatar hasError={isError} />}
      <MFlex direction="column" minW={0}>
        <MBox
          maxW="100%"
          {...(isError && {
            px: 4,
            py: 3,
            borderRadius: '2xl',
            borderTopLeftRadius: 'md',
            bg: 'tRed.50',
            border: '1px solid',
            borderColor: 'tRed.cosmos',
          })}
        >
          <FormatText
            text={message.content}
            textProps={ASSISTANT_TEXT_PROPS}
            setTablesCsv={setTablesCsv}
          />
          {isError && <MessageErrorDetails message={message} />}
        </MBox>
        <MFlex alignItems="center" gap={2} mt={1} ml={isError ? 2 : 0}>
          {/* First, so hidden actions after it don't leave it floating. */}
          <MText fontSize="xxs" color="fg.subtle">
            {format(new Date(message.timestamp), 'h:mm a')}
          </MText>
          <MFlex
            alignItems="center"
            gap={1}
            {...revealOnHoverProps(isLatestReply || isError)}
          >
            {!isError && canCopyClipboard && message.content && (
              <MTooltip
                label={isCopied ? 'Copied' : 'Copy response'}
                hasArrow
                placement="top"
                closeOnClick={false}
              >
                <MIconButton
                  aria-label="Copy response"
                  icon={
                    <Icon
                      as={isCopied ? MdCheck : MdContentCopy}
                      boxSize="14px"
                    />
                  }
                  size="xs"
                  variant="ghost"
                  color={isCopied ? 'action.success' : 'fg.muted'}
                  _hover={{ color: 'fg.strong', bg: 'bg.hover' }}
                  onClick={handleCopy}
                />
              </MTooltip>
            )}
            {downloadAction}
          </MFlex>
        </MFlex>
      </MFlex>
    </MFlex>
  );
};

export const MessageItem = memo(MessageItemComponent);
